// v2 gold-rock — a split host stone: a dark boulder cracked into two leaning
// lobes, the fracture between them filled with a zig-zag seam of bright gold
// wedges that catches the eye from far off, plus a couple of spilled nuggets and
// warm-flecked scree at the foot. Each variant re-opens the crack at a different
// angle. Ported 1:1 from age-of-mcp's v2/gold-rock.ts, using the library's
// shared `mineral` (h01, lerp, scree) helpers.
import * as THREE from "three";
import { defineModel, mineral, type Part } from "../../lib";

const HOST   = "#4f4b44";   // dark umber host rock
const HOST2  = "#5c5850";
const GOLD   = "#f2c43c";   // bright seam gold
const GOLD2  = "#ffd95a";   // glint
const GOLD3  = "#d9a52a";   // shaded gold

function goldRockParts(v: number): Part[] {
  const seed = v * 13 + 7;
  const yaw  = v * 2.3;
  const gap  = mineral.lerp(0.1, 0.15, mineral.h01(seed, 0));   // how far the crack opens
  const parts: Part[] = [
    // the two split lobes, leaning apart around the gold seam
    { geometry: new THREE.IcosahedronGeometry(0.21, 0), color: HOST,
      position: [-gap, 0.18, 0],
      rotation: [0.1, yaw, 0.3],
      scale: [1.0, 1.25, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.18, 0), color: HOST2,
      position: [gap + 0.02, 0.15, 0.02],
      rotation: [-0.08, yaw + 0.9, -0.34],
      scale: [0.95, 1.2, 1.0] },
  ];

  // The exposed seam: a zig-zag of gold wedges climbing the fracture.
  const steps = 4;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    parts.push({
      geometry: new THREE.TetrahedronGeometry(mineral.lerp(0.055, 0.085, mineral.h01(seed, 10 + i)), 0),
      color: [GOLD, GOLD2, GOLD3][i % 3]!,
      position: [
        mineral.lerp(-0.025, 0.03, t) + (i % 2 === 0 ? -0.015 : 0.02),
        mineral.lerp(0.06, 0.3, t),
        mineral.lerp(0.03, -0.04, t),
      ],
      rotation: [mineral.h01(seed, 20 + i) * 1.2, yaw + i * 1.1, mineral.h01(seed, 30 + i) * 1.2],
      scale: [1.0, 1.35, 0.9],
    });
  }

  // Two spilled nuggets where the seam weathered out.
  for (let i = 0; i < 2; i++) {
    const ang = yaw + 0.8 + i * 2.6;
    parts.push({
      geometry: new THREE.IcosahedronGeometry(mineral.lerp(0.045, 0.06, mineral.h01(seed, 40 + i)), 0),
      color: i === 0 ? GOLD2 : GOLD3,
      position: [Math.cos(ang) * 0.26, 0.045, Math.sin(ang) * 0.26],
      rotation: [0, ang, 0.3],
      scale: [1.1, 0.75, 1.0],
    });
  }

  parts.push(...mineral.scree(seed, 3, [0.3, 0.38], [0.03, 0.05], [HOST, HOST2, GOLD3]));
  return parts;
}

export const goldRockV2 = defineModel({
  id: "gold-rock-v2",
  batchId: "fable",
  name: "Gold rock (v2)",
  tags: ["scenery","gold","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly gold rock.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.42,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => goldRockParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => goldRockParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => goldRockParts(2) },
  ],
  animations: [],
});
